export type WsNotification = {
  eventId: string;
  status: string;
  error?: string;
  executionId?: string;
  workflowId?: string;
  createdId?: string;
  workflowResults?: Record<string, { status: string; error?: string; results?: Record<string, unknown> }>;
  createdEntities?: Array<{ workflowId: string; createdId: string }>;
  results?: Record<string, unknown>;
};

let ws: WebSocket | null = null;
let connectionId: string | null = null;
const pending = new Map<string, (n: WsNotification) => void>();

// API Gateway closes a WebSocket after 10 minutes with nothing *from the client* (its pushes don't
// count), which cut every long feed import off mid-run. Any frame resets the clock; '{}' is the
// same one onopen sends, and its connectionId reply is ignored once we have one.
const KEEPALIVE_MS = 5 * 60_000;

// Buffer for WS messages that arrived before onEvent was called — handles the race where
// a fast Lambda completes and the WS push beats the emitEvent promise returning the eventId.
const BUFFER_MAX = 50;
const BUFFER_TTL_MS = 60_000;
const earlyBuffer = new Map<string, { notification: WsNotification; receivedAt: number }>();

function pruneBuffer() {
  const cutoff = Date.now() - BUFFER_TTL_MS;
  for (const [id, entry] of earlyBuffer) {
    if (entry.receivedAt < cutoff) earlyBuffer.delete(id);
  }
  if (earlyBuffer.size > BUFFER_MAX) {
    const oldest = [...earlyBuffer.entries()].sort((a, b) => a[1].receivedAt - b[1].receivedAt);
    for (const [id] of oldest.slice(0, earlyBuffer.size - BUFFER_MAX)) earlyBuffer.delete(id);
  }
}

export function initWs(endpoint: string, getToken: () => string | null): void {
  if (!endpoint) return;
  function connect() {
    const token = getToken();
    if (!token) { setTimeout(connect, 3000); return; }
    const socket = new WebSocket(`${endpoint}?token=${encodeURIComponent(token)}`);
    ws = socket;
    let keepalive: ReturnType<typeof setInterval> | undefined;
    socket.onopen = () => {
      socket.send('{}');
      keepalive = setInterval(() => { if (socket.readyState === WebSocket.OPEN) socket.send('{}'); }, KEEPALIVE_MS);
    };
    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data as string) as Record<string, unknown>;
        if (typeof data['connectionId'] === 'string' && !connectionId) {
          connectionId = data['connectionId'] as string;
        } else if (typeof data['eventId'] === 'string') {
          const evId = data['eventId'] as string;
          const notification = data as unknown as WsNotification;
          const handler = pending.get(evId);
          if (handler) {
            pending.delete(evId);
            handler(notification);
          } else {
            pruneBuffer();
            earlyBuffer.set(evId, { notification, receivedAt: Date.now() });
          }
        }
      } catch {
        // Ignore malformed / non-JSON frames.
      }
    };
    socket.onclose = () => {
      clearInterval(keepalive);
      connectionId = null;
      // Results are pushed to the connection that made the request, so anything still waiting on the dropped one will never
      // arrive. Fail it now rather than leave the caller to time out.
      for (const [id, handler] of [...pending]) { pending.delete(id); handler({ eventId: id, status: 'disconnected' }); }
      setTimeout(connect, 3000);
    };
    socket.onerror = () => { socket.close(); };
  }
  connect();
}

export function getConnectionId(): string | null { return connectionId; }

import { globalToast } from './globalToast';

export function waitForConnection(timeoutMs = 3000): Promise<string | null> {
  if (connectionId) return Promise.resolve(connectionId);
  return new Promise(resolve => {
    const deadline = Date.now() + timeoutMs;
    const check = () => {
      if (connectionId) { resolve(connectionId); return; }
      if (Date.now() >= deadline) { resolve(null); return; }
      setTimeout(check, 100);
    };
    check();
  });
}

/**
 * The connection id for one step of a long loop (a feed import's rows): a reconnect mid-run gets a
 * new id, and results addressed to the old one never arrive, so a loop must not hold one id for
 * its whole run. Throws if no connection comes back, failing just that step.
 */
export async function currentConnection(): Promise<string> {
  const connId = await waitForConnection(10_000);
  if (!connId) throw new Error('Unable to connect to server');
  return connId;
}

export async function waitForConnectionWithToast(timeoutMs = 10_000): Promise<string | null> {
  if (connectionId) return connectionId;
  const toastId = globalToast.show('Connecting to server...');
  const connId = await waitForConnection(timeoutMs);
  globalToast.dismiss(toastId);
  return connId;
}

export function onEvent(eventId: string, handler: (n: WsNotification) => void, timeoutMs = 15_000): void {
  const buffered = earlyBuffer.get(eventId);
  if (buffered) {
    earlyBuffer.delete(eventId);
    handler(buffered.notification);
    return;
  }

  pending.set(eventId, handler);
  setTimeout(() => {
    if (pending.has(eventId)) {
      pending.delete(eventId);
      handler({ eventId, status: 'timeout' });
    }
  }, timeoutMs);
}
